/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import capitalize from 'lodash/capitalize';

// Lib
import { apiClient } from '@lib/api-client';

// Types
import {
	TApiResponseGetOrganizationsPopulated,
	TParsedOrganization,
	TApiResponseOrganizationPopulated,
} from '../types';
import {
	TApiParams,
	TParams,
} from '@global-types/app/app';

// Utils
import { formatDate } from '@utils/functions';
import { getCountryName } from '@utils/countries';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getOrganizations = (
	params: TApiParams
): Promise<TApiResponseGetOrganizationsPopulated> => {
	return apiClient.get('/organizations', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate: [
				'organization',
				'owner',
				'verifiedBy',
				'logo',
			],
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useOrganizationsApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { data, isFetching, isFetched, isPending } =
		useQuery({
			queryKey: [
				queryKeys.app.organizations.list.key,
				{
					page: page,
					size: size,
					queryParams: queryParams,
					sortParams: sortParams,
				},
			],
			queryFn: () =>
				getOrganizations({
					page: page,
					pageSize: size,
					queryParams: queryParams,
					sortParams: sortParams,
				}),
			staleTime: 0,
		});

	const updateList = (
		Organizations: TApiResponseOrganizationPopulated[]
	) =>
		Organizations.reduce(
			(
				accumulator: TParsedOrganization[],
				currentItem: TApiResponseOrganizationPopulated
			) => {
				const modifiedItem: TParsedOrganization = {
					...currentItem,
					countryCode: currentItem?.location?.countryCode
						? getCountryName(
								currentItem.location.countryCode
							)
						: NOT_APPLICABLE,
					place: currentItem?.location?.place
						? currentItem.location.place
						: NOT_APPLICABLE,
					organization: currentItem?.organization?.name
						? currentItem.organization.name
						: NOT_APPLICABLE,
					organizationId: currentItem?.organization?.id
						? currentItem.organization.id
						: NOT_APPLICABLE,
					createdAt: currentItem?.createdAt
						? formatDate(
								currentItem.createdAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					updatedAt: currentItem?.updatedAt
						? formatDate(
								currentItem.updatedAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					verifiedBy:
						!isNil(currentItem.verified) &&
						!isNil(currentItem.verifiedBy)
							? `${currentItem.verifiedBy.firstName} ${currentItem.verifiedBy.lastName}`
							: NOT_APPLICABLE,
					verifiedAt:
						!isNil(currentItem.verified) &&
						!isNil(currentItem.verifiedAt)
							? formatDate(
									currentItem.verifiedAt,
									'DD/MM/YYYY'
								)
							: NOT_APPLICABLE,
					legalIdentifier: currentItem?.legalIdentifier
						? currentItem.legalIdentifier
						: NOT_APPLICABLE,
					ownerName: currentItem?.owner
						? `${capitalize(currentItem.owner.firstName)} ${currentItem.owner.lastName}`
						: NOT_APPLICABLE,
					ownerEmail: currentItem?.owner
						? `${currentItem.owner.email}`
						: NOT_APPLICABLE,
					ownerPhone: currentItem?.owner?.phoneNumber
						? `${currentItem.owner.phoneNumber}`
						: NOT_APPLICABLE,
				};
				accumulator.push(modifiedItem);

				return accumulator;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		isPending,
		total: data?.total ?? 0,
		page: data?.page ?? 0,
		pageSize: data?.pageSize ?? 0,
		totalPages: data?.totalPages ?? 0,
	};
};
